import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { PlayerAvatar } from "./PlayerAvatar";
import { api, formatApiError } from "../lib/api";
import { computeRadar, winRate } from "./helpers";
import { toast } from "sonner";
import {
  RadarChart, PolarGrid, PolarAngleAxis, Radar, ResponsiveContainer,
  AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";
import { Loader2, Users2, History, Camera, TrendingUp, Sparkles, RefreshCw, Flame, Award, Trophy } from "lucide-react";
import { BadgeIcon, toneClasses } from "./badges";
import { ShareProfile } from "./ShareProfile";
import { Share2, Star } from "lucide-react";

function Ring({ value }) {
  const deg = (value / 100) * 360;
  return (
    <div
      className="relative w-24 h-24 rounded-full flex items-center justify-center shrink-0"
      style={{ background: `conic-gradient(#10B981 ${deg}deg, #1f2937 ${deg}deg)` }}
    >
      <div className="w-[76px] h-[76px] rounded-full bg-[#0f172a] flex flex-col items-center justify-center">
        <span className="font-mono font-black text-xl text-emerald-400">{value}%</span>
        <span className="text-[9px] uppercase tracking-wider text-slate-500">Win rate</span>
      </div>
    </div>
  );
}

export function PlayerDetailDialog({ playerId, onClose, isAdmin, onPhotoChange }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const [ai, setAi] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [records, setRecords] = useState(null);
  const [elo, setElo] = useState(null);
  const [badges, setBadges] = useState([]);
  const [showShare, setShowShare] = useState(false);
  const [rating, setRating] = useState(null);

  const loadAi = (refresh = false) => {
    if (!playerId) return;
    setAiLoading(true);
    api
      .get(`/players/${playerId}/ai-comment${refresh ? "?refresh=true" : ""}`)
      .then((r) => setAi(r.data))
      .catch(() => setAi({ error: true }))
      .finally(() => setAiLoading(false));
  };

  useEffect(() => {
    if (!playerId) return;
    setLoading(true);
    setData(null);
    setAi(null);
    setRecords(null);
    setBadges([]);
    setShowShare(false);
    api
      .get(`/players/${playerId}/stats`)
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
    api.get(`/players/${playerId}/records`).then((r) => setRecords(r.data)).catch(() => {});
    api.get(`/elo`).then((r) => setElo((r.data || []).find((x) => x.player_id === playerId))).catch(() => {});
    api.get(`/achievements`).then((r) => setBadges(r.data.by_player?.[playerId] || [])).catch(() => setBadges([]));
    setRating(null);
    api.get(`/votes/season`).then((r) => setRating((r.data || []).find((x) => x.player_id === playerId) || null)).catch(() => {});
    loadAi(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playerId]);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Seleziona un file immagine");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/players/${playerId}/photo`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Foto aggiornata");
      onPhotoChange?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setUploading(false);
    }
  };

  const s = data?.stats;
  const wr = s ? winRate(s) : 0;
  const radar = s ? computeRadar(s) : [];
  const progression = data?.progression || [];

  return (
    <Dialog open={!!playerId} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="bg-slate-900/95 backdrop-blur-xl border border-white/10 text-slate-100 max-w-2xl max-h-[90vh] overflow-y-auto"
        data-testid="player-detail-dialog"
      >
        {loading || !data ? (
          <div className="py-16 flex justify-center">
            <DialogTitle className="sr-only">Caricamento profilo giocatore</DialogTitle>
            <Loader2 className="w-8 h-8 animate-spin text-emerald-400" />
          </div>
        ) : (
          <>
            <DialogHeader>
              <div className="flex items-center gap-4">
                <div className="relative">
                  <PlayerAvatar id={data.player.id} name={data.player.name} size={56} />
                  {isAdmin && (
                    <button
                      data-testid="upload-photo-btn"
                      onClick={() => fileRef.current?.click()}
                      disabled={uploading}
                      title="Carica foto"
                      className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-emerald-500 hover:bg-emerald-400 text-slate-950 flex items-center justify-center border-2 border-[#0f172a]"
                    >
                      {uploading ? (
                        <Loader2 className="w-3 h-3 animate-spin" />
                      ) : (
                        <Camera className="w-3 h-3" />
                      )}
                    </button>
                  )}
                  <input
                    ref={fileRef}
                    type="file"
                    accept="image/*"
                    onChange={onFile}
                    className="hidden"
                    data-testid="photo-file-input"
                  />
                </div>
                <div>
                  <DialogTitle className="font-display uppercase tracking-tight text-2xl">
                    {data.player.name}
                  </DialogTitle>
                  <div className="font-mono text-xs text-emerald-400">
                    Posizione #{s.position} · {s.points} punti
                    {elo && <span className="text-slate-500"> · ELO {elo.elo}</span>}
                  </div>
                </div>
                <button
                  data-testid="open-share-btn"
                  onClick={() => setShowShare((v) => !v)}
                  title="Condividi profilo"
                  className="ml-auto flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-slate-800 hover:bg-slate-700 border border-slate-700 text-slate-200 text-xs font-semibold uppercase tracking-wide"
                >
                  <Share2 className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">Condividi</span>
                </button>
              </div>
            </DialogHeader>

            {showShare && (
              <div className="ap-glass rounded-2xl p-4 mt-1" data-testid="share-section">
                <ShareProfile
                  playerId={data.player.id}
                  name={data.player.name}
                  subtitle={`#${s.position} in classifica · ${s.points} punti`}
                />
              </div>
            )}

            <div className="grid sm:grid-cols-2 gap-5 pt-2">
              <div className="rounded-2xl bg-slate-900/60 border border-slate-800 p-3 h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <RadarChart data={radar} outerRadius="72%">
                    <PolarGrid stroke="#334155" />
                    <PolarAngleAxis
                      dataKey="axis"
                      tick={{ fill: "#94a3b8", fontSize: 11, fontWeight: 600 }}
                    />
                    <Radar dataKey="v" stroke="#10B981" fill="#10B981" fillOpacity={0.4} />
                  </RadarChart>
                </ResponsiveContainer>
              </div>

              <div className="flex flex-col items-center justify-center gap-4 rounded-2xl bg-slate-900/60 border border-slate-800 p-4">
                <Ring value={wr} />
                <div className="grid grid-cols-3 gap-2 w-full text-center">
                  <div>
                    <div className="font-mono font-black text-lg text-white">{s.played}</div>
                    <div className="text-[10px] uppercase text-slate-500">Giocate</div>
                  </div>
                  <div>
                    <div className="font-mono font-black text-lg text-emerald-400">{s.wins}</div>
                    <div className="text-[10px] uppercase text-slate-500">Vinte</div>
                  </div>
                  <div>
                    <div className="font-mono font-black text-lg text-rose-400">{s.losses}</div>
                    <div className="text-[10px] uppercase text-slate-500">Perse</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="ap-glass rounded-xl p-3 mt-3 flex items-center gap-3" data-testid="player-rating-section">
              <Star className="w-5 h-5 text-amber-400 fill-amber-400" />
              <div className="flex-1">
                <div className="text-[10px] uppercase tracking-wider text-slate-500">Media voto pagella</div>
                <div className="text-xs text-slate-400">
                  {rating ? `${rating.count} ${rating.count === 1 ? "voto" : "voti"} in ${rating.matches} ${rating.matches === 1 ? "incontro" : "incontri"}` : "Nessun voto ricevuto"}
                </div>
              </div>
              <span className="font-mono font-black text-2xl text-amber-400" data-testid="player-rating-avg">
                {rating ? rating.avg.toFixed(1) : "—"}
              </span>
            </div>

            {records && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-3" data-testid="records-section">
                <div className="ap-glass rounded-xl p-3 text-center">
                  <Flame className="w-4 h-4 text-orange-400 mx-auto mb-1" />
                  <div className="font-mono font-black text-lg text-white">{records.win_streak}</div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500">Serie V</div>
                </div>
                <div className="ap-glass rounded-xl p-3 text-center">
                  <Trophy className="w-4 h-4 text-emerald-400 mx-auto mb-1" />
                  <div className="font-mono font-black text-lg text-emerald-400">
                    {records.best_win ? records.best_win.score : "—"}
                  </div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500">Miglior vittoria</div>
                </div>
                <div className="ap-glass rounded-xl p-3 text-center">
                  <Award className="w-4 h-4 text-cyan-400 mx-auto mb-1" />
                  <div className="font-bold text-sm text-white truncate">
                    {records.best_partner ? records.best_partner.name : "—"}
                  </div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500">Compagno top</div>
                </div>
                <div className="ap-glass rounded-xl p-3 text-center">
                  <History className="w-4 h-4 text-rose-400 mx-auto mb-1" />
                  <div className="font-mono font-black text-lg text-rose-400">
                    {records.worst_loss ? records.worst_loss.score : "—"}
                  </div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500">Ko peggiore</div>
                </div>
              </div>
            )}

            {badges.length > 0 && (
              <div className="pt-3" data-testid="player-badges-section">
                <div className="flex items-center gap-2 mb-2">
                  <Award className="w-4 h-4 text-amber-400" />
                  <span className="font-display uppercase tracking-wide text-sm text-slate-300">
                    Riconoscimenti
                  </span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {badges.map((b) => {
                    const tone = toneClasses(b.tone);
                    return (
                      <div
                        key={b.id}
                        data-testid={`player-badge-${b.id}`}
                        className={`flex items-center gap-2 px-3 py-1.5 rounded-full border ${tone.chip}`}
                      >
                        <BadgeIcon icon={b.icon} className={`w-3.5 h-3.5 ${tone.text}`} />
                        <span className="text-xs font-semibold text-slate-200">{b.title}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {(aiLoading || ai) && (
              <div className="pt-3" data-testid="ai-comment-section">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-fuchsia-400" />
                    <span className="font-display uppercase tracking-wide text-sm text-slate-300">
                      Analisi AI
                    </span>
                  </div>
                  {isAdmin && ai && ai.has_data && (
                    <button
                      data-testid="ai-comment-refresh"
                      onClick={() => loadAi(true)}
                      disabled={aiLoading}
                      className="flex items-center gap-1 text-[11px] uppercase tracking-wide text-slate-400 hover:text-fuchsia-400 disabled:opacity-50"
                    >
                      <RefreshCw className={`w-3.5 h-3.5 ${aiLoading ? "animate-spin" : ""}`} />
                      Rigenera
                    </button>
                  )}
                </div>

                {aiLoading ? (
                  <div className="ap-glass rounded-2xl p-5 flex items-center gap-3 text-slate-400 text-sm">
                    <Loader2 className="w-4 h-4 animate-spin text-fuchsia-400" />
                    Generazione del commento in corso…
                  </div>
                ) : ai?.error ? (
                  <div className="ap-glass rounded-2xl p-4 text-sm text-slate-400 flex items-center justify-between">
                    Commento non disponibile.
                    <button
                      onClick={() => loadAi(true)}
                      className="text-fuchsia-400 hover:text-fuchsia-300 text-xs uppercase tracking-wide"
                    >
                      Riprova
                    </button>
                  </div>
                ) : ai?.has_data ? (
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div className="ap-glass rounded-2xl p-4 border-l-2 border-l-emerald-500/60">
                      <div className="text-[10px] uppercase tracking-widest text-emerald-400 mb-1.5">
                        Prestazione individuale
                      </div>
                      <p className="text-sm text-slate-200 leading-relaxed">{ai.singolo}</p>
                    </div>
                    <div className="ap-glass rounded-2xl p-4 border-l-2 border-l-cyan-500/60">
                      <div className="text-[10px] uppercase tracking-widest text-cyan-400 mb-1.5">
                        Rendimento in coppia
                      </div>
                      <p className="text-sm text-slate-200 leading-relaxed">{ai.coppia}</p>
                    </div>
                  </div>
                ) : (
                  <div className="ap-glass rounded-2xl p-4 text-sm text-slate-500">
                    Il commento AI sarà disponibile dopo la prima partita giocata.
                  </div>
                )}
              </div>
            )}

            {progression.length > 0 && (
              <div className="pt-3">
                <div className="flex items-center gap-2 mb-2">
                  <TrendingUp className="w-4 h-4 text-emerald-400" />
                  <span className="font-display uppercase tracking-wide text-sm text-slate-300">
                    Andamento punti
                  </span>
                </div>
                <div className="ap-glass rounded-2xl p-3 h-52">
                  <ResponsiveContainer width="100%" height="100%" minHeight={160}>
                    <AreaChart data={progression} margin={{ top: 8, right: 12, left: -18, bottom: 0 }}>
                      <defs>
                        <linearGradient id="apGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#10B981" stopOpacity={0.5} />
                          <stop offset="100%" stopColor="#10B981" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" vertical={false} />
                      <XAxis dataKey="label" tick={{ fill: "#94a3b8", fontSize: 11 }} axisLine={false} tickLine={false} />
                      <YAxis tick={{ fill: "#94a3b8", fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} />
                      <Tooltip
                        contentStyle={{ background: "#0f172a", border: "1px solid #334155", borderRadius: 10, color: "#fff" }}
                        labelStyle={{ color: "#10B981" }}
                        formatter={(v, n) => [v, n === "cumulative" ? "Totale" : "Giornata"]}
                      />
                      <Area
                        type="monotone"
                        dataKey="cumulative"
                        stroke="#10B981"
                        strokeWidth={2.5}
                        fill="url(#apGrad)"
                        dot={{ r: 3, fill: "#10B981" }}
                        activeDot={{ r: 5 }}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}

            {data.partners.length > 0 && (
              <div className="pt-3">
                <div className="flex items-center gap-2 mb-2">
                  <Users2 className="w-4 h-4 text-blue-400" />
                  <span className="font-display uppercase tracking-wide text-sm text-slate-300">
                    Sinergia con i compagni
                  </span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {data.partners.map((p) => (
                    <div
                      key={p.partner_id}
                      className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-slate-900 border border-slate-800"
                    >
                      <PlayerAvatar id={p.partner_id} name={p.name} size={22} ring={false} />
                      <span className="text-xs text-slate-300">{p.name}</span>
                      <span className="font-mono text-xs font-bold text-emerald-400">
                        {p.wins}/{p.played}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="pt-3">
              <div className="flex items-center gap-2 mb-2">
                <History className="w-4 h-4 text-amber-400" />
                <span className="font-display uppercase tracking-wide text-sm text-slate-300">
                  Storico partite
                </span>
              </div>
              <div className="space-y-2">
                {data.history.map((h, i) => (
                  <div
                    key={i}
                    className="flex items-center justify-between gap-3 px-3 py-2 rounded-xl bg-slate-900/60 border border-slate-800"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <span
                        className={`w-7 h-7 rounded-lg flex items-center justify-center font-mono font-bold text-xs shrink-0 ${
                          h.result === "V"
                            ? "bg-emerald-500/20 text-emerald-400"
                            : h.result === "P"
                            ? "bg-rose-500/20 text-rose-400"
                            : "bg-slate-800 text-slate-500"
                        }`}
                      >
                        {h.result || "–"}
                      </span>
                      <div className="min-w-0">
                        <div className="text-sm text-slate-200 truncate">
                          con <span className="text-white font-semibold">{h.partner}</span> vs{" "}
                          {h.opponents.join(" / ")}
                        </div>
                        <div className="text-[11px] text-slate-500 font-mono">
                          G{h.matchday} · Campo {h.court}
                        </div>
                      </div>
                    </div>
                    {h.score ? (
                      <span className="font-mono font-bold text-emerald-400 shrink-0">{h.score}</span>
                    ) : (
                      <span className="text-xs text-amber-400 shrink-0">Da giocare</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
